/**
 * Model Endpoints REST API client — /api/model-endpoints/*（model_endpoints 模組）
 * + /api/llm-runtime/apply（rag_app 接線端點）。
 *
 * 契約：model_endpoints/API_GUIDE.md、HANDOFF.md；
 * 前端整合設計：docs/MODEL_MANAGEMENT_INTEGRATION_PLAN.md。
 */

const BASE = '/api/model-endpoints';

/** 後端 HTTPException(detail={reason: …}) 的結構化錯誤。 */
export class ModelEndpointError extends Error {
  reason?: string;
  status: number;
  detail?: unknown;

  constructor(status: number, detail: unknown) {
    const reason =
      typeof detail === 'object' && detail !== null
        ? (detail as Record<string, unknown>).reason
        : undefined;
    super(typeof reason === 'string' ? reason : `HTTP ${status}`);
    this.status = status;
    this.reason = typeof reason === 'string' ? reason : undefined;
    this.detail = detail;
  }
}

const req = (method: string, path: string, body?: unknown) =>
  fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }).then(async (res) => {
    if (!res.ok) {
      const data = await res.json().catch(() => null);
      throw new ModelEndpointError(
        res.status,
        (data as Record<string, unknown> | null)?.detail ?? data ?? res.statusText,
      );
    }
    return res.json();
  });

// ---- 型別（對齊後端契約，欄位皆為後端命名） --------------------------------

export interface Capabilities {
  supported_backends: string[];
  platform: string;
}

export interface LlmRoleConfig {
  backend: string;
  provider: string;
  base_url: string;
  api_key: string;
  model: string;
  context_total: number | null;
  output_reserve: number | null;
  max_input_tokens: number | null;
  context_source: string;
  selected_by: string;
  status: 'running' | 'starting';
  supports_vision?: boolean;
  vision_source?: string;
}

export interface EndpointConfig {
  ok: boolean;
  /** ok=false 時：no_model_running（409）| backend_unreachable（503） */
  reason?: string;
  detail?: string;
  llm?: LlmRoleConfig | null;
  vlm?: LlmRoleConfig | null;
  warnings?: { code: string; [k: string]: unknown }[];
  candidates?: { backend: string; model: string; selected_as: string[] }[];
  queried_at?: string;
}

export interface DiskEntry {
  label: string;
  path: string;
  total_bytes: number;
  free_bytes?: number;
  [k: string]: unknown;
}

export interface Resources {
  disk: DiskEntry[];
  ram: { total_bytes: number; available_bytes: number; used_pct: number };
  gpus: { total_vram_bytes: number; free_vram_bytes: number; [k: string]: unknown }[];
  loaded_models: {
    backend: string;
    model: string | null;
    vram_bytes: number | null;
    vram_ratio: number | null;
    placement: string;
  }[];
  limits: { ollama_max_loaded_models: number | null; source: string };
  assumed_ctx: { value: number; source: string };
  detection: { vram_source: string; notes: string[] };
}

/** Job 快照/SSE 事件（§3.3；兩者同形）。 */
export interface JobSnapshot {
  job_id: string;
  kind: string;
  backend: string;
  model: string;
  state: 'pending' | 'running' | 'success' | 'failed' | 'cancelled';
  message: string;
  error: string | null;
  completed: number | null;
  total: number | null;
  percent: number | null;
  speed_bps: number | null;
  eta_sec: number | null;
  result: Record<string, unknown> | null;
  created_at: string;
}

export const TERMINAL_JOB_STATES: JobSnapshot['state'][] = ['success', 'failed', 'cancelled'];

// ---- API ------------------------------------------------------------------

export const modelEndpointsApi = {
  capabilities: (): Promise<Capabilities> => req('GET', '/capabilities'),

  /** 409（沒模型在跑）/ 503（後端不可達）的 body 也是契約的一部分，不是錯誤。
   *  refresh=true 讓後端略過 1.5s TTL 快取（模型啟停動作後的刷新用）。 */
  config: (refresh = false): Promise<EndpointConfig> =>
    fetch(`${BASE}/config${refresh ? '?refresh=1' : ''}`, { credentials: 'include' }).then(async (res) => {
      const data = await res.json().catch(() => null);
      if (data && typeof data === 'object' && 'ok' in data) return data as EndpointConfig;
      throw new ModelEndpointError(res.status, data ?? res.statusText);
    }),

  resources: (): Promise<Resources> => req('GET', '/resources'),

  /** 已安裝模型（ollama /api/tags；vLLM 為模型資料夾清單）。 */
  models: (backend: string): Promise<Record<string, unknown>[]> =>
    req('GET', `/${backend}/models`).then((d) => d.models ?? d),

  /** 運行中模型（ollama /api/ps；vLLM /v1/models）。 */
  runningModels: (backend: string): Promise<Record<string, unknown>[]> =>
    req('GET', `/${backend}/models/running`).then((d) => d.models ?? d),

  pull: (backend: string, model: string): Promise<{ job_id: string }> =>
    req('POST', `/${backend}/models/pull`, { model }),

  start: (
    backend: string,
    model: string,
    context?: number,
  ): Promise<{ job_id: string; existing: boolean }> =>
    req('POST', `/${backend}/models/start`, { model, ...(context ? { context } : {}) }),

  stop: (backend: string, model: string): Promise<{ job_id: string; existing: boolean }> =>
    req('POST', `/${backend}/models/stop`, { model }),

  deleteModel: (backend: string, model: string): Promise<Record<string, unknown>> =>
    req('POST', `/${backend}/models/delete`, { model, confirm: true }),

  jobs: (): Promise<{ jobs: JobSnapshot[] }> => req('GET', '/jobs'),

  job: (jobId: string): Promise<JobSnapshot> => req('GET', `/jobs/${jobId}`),

  cancelJob: (jobId: string): Promise<Record<string, unknown>> =>
    req('DELETE', `/jobs/${jobId}`),

  /**
   * rag_app 接線端點：啟動模型 job 成功後呼叫,立即把「實際在跑」的模型套用到
   * 問答並寫回 config.env（記住上次選擇）。就算漏呼叫,下一次問答前也會自動收斂。
   */
  applyLlmRuntime: (): Promise<{
    applied: boolean;
    reason?: string;
    model?: string;
    base_url?: string;
    /** 一併同步的視覺模型（運行中的模型不具視覺能力時為空 → VLM 維持現狀） */
    vlm_model?: string | null;
    persisted?: boolean;
  }> =>
    fetch('/api/llm-runtime/apply', {
      method: 'POST',
      credentials: 'include',
    }).then(async (res) => {
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(text || `HTTP ${res.status}`);
      }
      return res.json();
    }),

  /**
   * 引擎加速開關現況（OLLAMA_VULKAN）+ GPU 偵測。
   * OLLAMA_VULKAN: "1" = iGPU（Vulkan）加速；null = 自動（NVIDIA→CUDA，否則 CPU）
   */
  engineEnv: (): Promise<EngineEnv> =>
    fetch('/api/llm-runtime/engine-env', { credentials: 'include' }).then(
      async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          throw new ModelEndpointError(
            res.status,
            (data as Record<string, unknown> | null)?.detail ?? data ?? res.statusText,
          );
        }
        return res.json();
      },
    ),

  /** 模型管理總開關：false = 本程式完全不接管引擎（使用者自管 llama.cpp 等）。 */
  setManagement: (
    enabled: boolean,
  ): Promise<{ enabled: boolean; persisted: boolean; needs_restart: boolean }> =>
    fetch('/api/llm-runtime/management', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled }),
    }).then(async (res) => {
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new ModelEndpointError(
          res.status,
          (data as Record<string, unknown> | null)?.detail ?? data ?? res.statusText,
        );
      }
      return res.json();
    }),

  /** 設定引擎加速開關並立即重啟內附引擎（勾選即生效；模型下次提問重載）。 */
  setEngineEnv: (
    vulkan: boolean,
  ): Promise<{
    ok: boolean;
    vulkan: boolean;
    restarted: boolean;
    persisted: boolean;
    detail?: string;
  }> =>
    fetch('/api/llm-runtime/engine-env', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ OLLAMA_VULKAN: vulkan ? '1' : null }),
    }).then(async (res) => {
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new ModelEndpointError(
          res.status,
          (data as Record<string, unknown> | null)?.detail ?? data ?? res.statusText,
        );
      }
      return res.json();
    }),
};

/** GET /api/llm-runtime/engine-env 的回應。 */
export interface EngineEnv {
  OLLAMA_VULKAN: string | null;
  gpus: string[];
  suggested_vulkan: boolean;
  engine_available: boolean;
  /** "user" = 使用者自裝版（接管模式）；"bundled" = 內附版；null = 未安裝 */
  engine_source?: 'user' | 'bundled' | null;
  /** 模型管理總開關；false = 使用者自管地端服務（llama.cpp 等），本程式不接管 */
  management_enabled?: boolean;
}
